import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import PersistLogin from "../../features/auth/PersistLogin";
import RequireAuth from "../../features/auth/RequireAuth";

const Layout = lazy(() => import("../layout/Layout"));
const AdminLayout = lazy(() => import("../layout/AdminLayout"));
const PartnerLayout = lazy(() => import("../layout/PartnerLayout"));

const HobaLandingPage = lazy(() => import("../../pages/MainPage"));
const Home = lazy(() => import("../../pages/Home"));
const About = lazy(() => import("../../pages/About"));
const WebsiteAboutPage = lazy(() => import("../../pages/WebsiteAboutPage"));
const Vehicles = lazy(() => import("../../pages/Vehicles"));
const FAQs = lazy(() => import("../../pages/FAQs"));
const BlogPage = lazy(() => import("../../pages/BlogPage"));
const ContactPage = lazy(() => import("../../pages/ContactPage"));
const HobaMobilityPage = lazy(() => import("../../pages/HobaMobilityPage"));
const VehicleDetails = lazy(
  () => import("../../features/vehicle/VehicleDetails"),
);
const PartnerLandingPage = lazy(() => import("../../pages/PartnerLandingPage"));
const CompanyLandingPage = lazy(() => import("../../pages/CompanyLandingPage"));
const InterstateDeliveryPage = lazy(
  () => import("../../pages/InterstateDeliveryPage"),
);
const SignIn = lazy(() => import("../../features/auth/SignIn"));
const OnBoardPartner = lazy(
  () => import("../../features/partner/OnBoardPartner"),
);
const PrivacyPolicy = lazy(() => import("../../pages/PrivacyPolicy"));
const TrackApplication = lazy(() =>
  import("../../features/riders/TrackApplication").then((m) => ({
    default: m.TrackApplication,
  })),
);
const RiderApplicationForm = lazy(
  () => import("../../features/riders/RiderApplication"),
);
const DashboardUi = lazy(() => import("../../pages/Dash01"));
const PageUnderConstruction = lazy(
  () => import("../../pages/PageUnderConstruction"),
);

const Dashboard = lazy(() => import("../../features/dash/Dashboard"));
const VehiclesDashboard = lazy(
  () => import("../../features/vehicle/VehiclesDashboard"),
);
const DashVehicleDetails = lazy(
  () => import("../../features/vehicle/DashVehicleDetails"),
);
const RidersDashboard = lazy(
  () => import("../../features/riders/RidersDashboard"),
);
const RiderDetails = lazy(() => import("../../features/riders/RiderDetails"));
const UserDashboard = lazy(() => import("../../features/user/UserDashboard"));
const DashUserDetails = lazy(
  () => import("../../features/user/DashUserDetails"),
);
const DashRoleDetails = lazy(
  () => import("../../features/user/DashRoleDetails"),
);
const DocumentVerification = lazy(
  () => import("../../features/request/DocumentVerification"),
);
const RiderApplicationDetails = lazy(
  () => import("../../features/riders/RiderApplicationDetails"),
);
const VehicleRequest = lazy(
  () => import("../../features/riders/VehicleRequest"),
);
const LoanAndLeaveDashboardPage = lazy(
  () => import("../../features/loan-leave/LoanAndLeaveDashboardPage"),
);
const VerificationDashboard = lazy(
  () => import("../../features/verification/VerificationDashboard"),
);
const PersonalLoanManagementDash = lazy(
  () => import("../../features/loan-leave/PersonalLoanManagementDash"),
);
const AssetLoanManagementDash = lazy(
  () => import("../../features/loan-leave/AssetLoanManagement"),
);
const DashLoanDetails = lazy(
  () => import("../../features/loan-leave/DashLoanDetails"),
);
const VehicleMaintenanceDash = lazy(
  () => import("../../features/maintenance/VehicleMaintenanceDash"),
);
const PaymentsDashboard = lazy(
  () => import("../../features/payments/PaymentsDashboard"),
);
const ExpenseDash = lazy(() => import("../../features/expense/ExpenseDash"));
const PartnerDashboard = lazy(
  () => import("../../features/partner/PartnerDashboard"),
);
const PartnerDetails = lazy(
  () => import("../../features/partner/PartnerDetails"),
);
const OrderDashboard = lazy(
  () => import("../../features/hoba-x/order/OrderDashboard"),
);
const PartnerMainDashboard = lazy(
  () => import("../../features/hoba-x/dashboard/PartnerDashboard"),
);
const BranchDashboard = lazy(
  () => import("../../features/hoba-x/branch/BranchDashboard"),
);
const StaffDashboard = lazy(
  () => import("../../features/hoba-x/staff/StaffDashboard"),
);
const PartnerOrderDashboard = lazy(
  () => import("../../features/hoba-x/order/PartnerOrdersDashboard"),
);

const RouteFallback: React.FC = () => (
  <div
    className="flex min-h-[40vh] items-center justify-center bg-white text-gray-500"
    aria-busy="true"
    aria-live="polite"
  >
    <span className="text-base">Loading…</span>
  </div>
);

const Router: React.FC = () => {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<HobaLandingPage />} />
          <Route path="riders" element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="about-us" element={<WebsiteAboutPage />} />
          <Route path="all-vehicles/:applicationId?" element={<Vehicles />} />
          <Route path="faqs" element={<FAQs />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="help-center" element={<ContactPage />} />
          <Route path="hoba-mobility" element={<HobaMobilityPage />} />
          <Route
            path="vehicle-details/:id/:applicationId?"
            element={<VehicleDetails />}
          />
          <Route path="partners" element={<PartnerLandingPage />} />
          <Route path="company" element={<CompanyLandingPage />} />
          <Route
            path="interstate-delivery"
            element={<InterstateDeliveryPage />}
          />
        </Route>

        <Route path="/sign-in" element={<SignIn />} />

        <Route path="/partners/onboard-partner" element={<OnBoardPartner />} />

        <Route path="/privacy-policy" element={<PrivacyPolicy />} />

        <Route path="/track-application" element={<TrackApplication />} />
        <Route
          path="/rider-application-form"
          element={<RiderApplicationForm />}
        />

        <Route path="/dashb" element={<DashboardUi />} />

        <Route element={<PersistLogin />}>
          <Route element={<RequireAuth allowedRoles={["admin", "Staff"]} />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="vehicles" element={<VehiclesDashboard />} />
              <Route path="vehicle/:id" element={<DashVehicleDetails />} />
              <Route path="riders" element={<RidersDashboard />} />
              <Route path="riders/:id" element={<RiderDetails />} />
              <Route path="users" element={<UserDashboard />} />
              <Route path="users/:id" element={<DashUserDetails />} />
              <Route path="roles/:id" element={<DashRoleDetails />} />

              <Route
                path="request-management/document-verification"
                element={<DocumentVerification />}
              />
              <Route
                path="request-management/:id"
                element={<RiderApplicationDetails />}
              />

              <Route
                path="request-management/vehicle-request"
                element={<VehicleRequest />}
              />
              <Route
                path="request-management/loan-leave-request"
                element={<LoanAndLeaveDashboardPage />}
              />
              <Route
                path="request-management/verification-payment"
                element={<VerificationDashboard />}
              />
              <Route
                path="loan-management/personal-loan"
                element={<PersonalLoanManagementDash />}
              />

              <Route
                path="loan-management/asset-loan"
                element={<AssetLoanManagementDash />}
              />

              <Route path="loan/:id" element={<DashLoanDetails />} />

              <Route path="maintenance" element={<VehicleMaintenanceDash />} />

              <Route path="payments" element={<PaymentsDashboard />} />

              <Route path="expense" element={<ExpenseDash />} />

              <Route path="partners" element={<PartnerDashboard />} />
              <Route path="partners/:id" element={<PartnerDetails />} />

              <Route path="orders" element={<OrderDashboard />} />

              <Route path="*" element={<PageUnderConstruction />} />
            </Route>
          </Route>

          <Route
            element={
              <RequireAuth allowedRoles={["LogisticPartner", "basic"]} />
            }
          >
            <Route path="/partner" element={<PartnerLayout />}>
              <Route index element={<PartnerMainDashboard />} />
              <Route path="branch" element={<BranchDashboard />} />
              <Route path="staff" element={<StaffDashboard />} />
              <Route path="orders" element={<PartnerOrderDashboard />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<PageUnderConstruction />} />
      </Routes>
    </Suspense>
  );
};

export default Router;
